import type { RoundEvent } from '@cs-shot/battle-sim';
import type { Fighter, Scene } from './types';
import { handOf } from './anchors';
import { muzzleOf } from './fighters';
import { spawnCasing, spawnMuzzlePuff } from './effects';
import { nameOf, teamOf } from './hud';
import { playSfx, RICOCHET_SFX } from '../game/sfx';
import { BURST, KNIFE_LUNGE, KNIFE_TRAVEL_FRAC, SHOT_SFX, now, type Mix } from './battleConfig';
import type { BattleEngineOpts, PlaybackState } from './playbackState';

type ShotEvent = Extract<RoundEvent, { type: 'shot' }>;
type DeathEvent = Extract<RoundEvent, { type: 'death' }>;
type HealEvent = Extract<RoundEvent, { type: 'heal' }>;
type ShieldEvent = Extract<RoundEvent, { type: 'shield' }>;
type KnifeEvent = Extract<RoundEvent, { type: 'knife' }>;
type SmokeEvent = Extract<RoundEvent, { type: 'smoke' }>;
type FlashEvent = Extract<RoundEvent, { type: 'flash' }>;
type GrenadeEvent = Extract<RoundEvent, { type: 'grenade' }>;

export interface Actors {
  applyShot: (ev: ShotEvent) => void;
  applyDeath: (ev: DeathEvent, n: number) => void;
  applyHeal: (ev: HealEvent) => void;
  applyShield: (ev: ShieldEvent) => void;
  applyKnife: (ev: KnifeEvent) => void;
  applySmokeThrow: (ev: SmokeEvent, land: number) => void;
  applyFlashThrow: (ev: FlashEvent, land: number) => void;
  applyFlashLand: (ev: FlashEvent, round: number) => void;
  applyGrenadeThrow: (ev: GrenadeEvent, land: number, toX: number, toY: number) => void;
  popDamage: (tg: { x: number; y: number }, amount: number, crit: boolean, shield?: boolean) => void;
  popAbsorb: (tg: { x: number; y: number }, amount: number) => void;
  drainHit: (tg: Fighter, damage: number, absorbed: number | undefined) => number;
  commit: (ev: RoundEvent) => void;
}

export const createActors = (sc: Scene, opts: BattleEngineOpts, state: PlaybackState, mix: Mix, commit: (ev: RoundEvent) => void): Actors => {
  const popDamage: Actors['popDamage'] = (tg, amount, crit, shield = false) => {
    sc.damages.push({ x: tg.x, y: tg.y, amount, crit, shield, t0: now(), life: 0.9 });
  };
  const popAbsorb: Actors['popAbsorb'] = (tg, amount) => {
    sc.damages.push({ x: tg.x, y: tg.y - 34, amount, crit: false, absorb: true, t0: now(), life: 0.9 });
  };

  const drainHit = (tg: Fighter, damage: number, absorbed: number | undefined): number => {
    const abs = Math.min(absorbed ?? 0, tg.shield);
    if (abs > 0) { tg.shield -= abs; popAbsorb(tg, abs); }
    return damage - abs;
  };

  const returnFire = (defender: Fighter, attacker: Fighter, dmg: number, attackerId: string, absorbed?: number) => {
    if (dmg <= 0) return;
    const back = Math.atan2(handOf(sc, attacker).y - handOf(sc, defender).y, attacker.x - handOf(sc, defender).x);
    defender.aim = back; defender.recoil = 1; defender.engagedId = attackerId;
    const from = muzzleOf(sc, defender, now());
    const hit = handOf(sc, attacker);
    sc.flashes.push({ x: from.x, y: from.y, angle: back, t0: now(), life: 0.07 });
    sc.bullets.push({ fromX: from.x, fromY: from.y, toX: hit.x, toY: hit.y, t0: now(), life: 0.12 });
    spawnCasing(sc, from.x, from.y, back, defender.dir, defender.y, defender.id === opts.getYouId(), now());
    spawnMuzzlePuff(sc, from.x, from.y, back, defender.y, now());
    sc.impacts.push({ x: hit.x, y: hit.y, t0: now(), life: 0.22 });
    const toHp = drainHit(attacker, dmg, absorbed);
    if (toHp > 0) popDamage(attacker, toHp, false);
    playSfx(SHOT_SFX[defender.weaponKey] ?? SHOT_SFX.ak, mix(0.32, defender.id, attackerId));
    if (!opts.replay && attackerId === opts.getYouId()) { opts.onYouHit(); playSfx('armor_hit', 0.5); }
  };

  const applyShot: Actors['applyShot'] = (ev) => {
    const s = sc.byId[ev.shooter]; const tg = sc.byId[ev.target];
    if (!s || !tg) return;
    s.engagedId = ev.target;
    const aimPt = handOf(sc, tg);
    s.aim = Math.atan2(aimPt.y - handOf(sc, s).y, tg.x - handOf(sc, s).x);
    s.recoil = 1;
    const from = muzzleOf(sc, s, now());
    sc.bullets.push({ fromX: from.x, fromY: from.y, toX: tg.x, toY: aimPt.y, t0: now(), life: 0.12 });
    sc.flashes.push({ x: from.x, y: from.y, angle: s.aim, t0: now(), life: 0.07 });
    spawnCasing(sc, from.x, from.y, s.aim, s.dir, s.y, ev.shooter === opts.getYouId(), now());
    spawnMuzzlePuff(sc, from.x, from.y, s.aim, s.y, now());
    playSfx(SHOT_SFX[s.weaponKey] ?? SHOT_SFX.ak, mix(0.4, ev.shooter, ev.target));
    const burst = BURST[s.weapon] ?? BURST.rifle;
    for (let i = 1; i < burst.count; i++) {
      state.pendingShots.push({ at: now() + i * burst.gap, fromX: from.x, fromY: from.y, toX: tg.x, toY: aimPt.y, key: s.weaponKey, angle: s.aim, shooter: ev.shooter, target: ev.target });
    }
    commit(ev);
    if (ev.hit) {
      sc.impacts.push({ x: tg.x, y: aimPt.y, t0: now(), life: 0.22 });
      const toHp = drainHit(tg, ev.damage, ev.absorbed);
      if (toHp > 0) popDamage(tg, toHp, ev.crit, ev.shield);
      if (!opts.replay && ev.target === opts.getYouId()) { opts.onYouHit(); playSfx('armor_hit', 0.5); }
    } else {
      const L = opts.getMissLabels();
      const label = ev.blind ? L.blind : ev.targetStance === 'maneuver' ? L.evade : L.miss;
      sc.damages.push({ x: tg.x, y: aimPt.y, amount: 0, crit: false, label, t0: now(), life: 0.8 });
      playSfx(RICOCHET_SFX[Math.floor(Math.random() * RICOCHET_SFX.length)], mix(0.3, ev.shooter, ev.target));
    }
    if (ev.reflect && ev.reflect > 0) returnFire(tg, s, ev.reflect, ev.shooter, ev.reflectAbsorbed);
  };

  const applyDeath: Actors['applyDeath'] = (ev, n) => {
    commit(ev);
    const f = sc.byId[ev.id];
    if (f && !f.dead) { f.dead = true; f.deadAt = n; }
    state.blindGlows.delete(ev.id);
    if (ev.headshot) playSfx('headshot', mix(0.5, ev.by, ev.id));
    if (!opts.replay && ev.id === opts.getYouId()) { opts.onYouDeath(); playSfx('death', 0.6); }
    if (ev.by) {
      sc.killFeed.push({
        by: nameOf(sc, ev.by), byTeam: teamOf(ev.by),
        victim: nameOf(sc, ev.id), victimTeam: teamOf(ev.id),
        headshot: ev.headshot, t0: n,
        byWeapon: ev.cause === 'knife' ? 'knife' : ev.cause === 'grenade' ? 'grenade' : (sc.byId[ev.by]?.weaponKey ?? 'ak'),
      });
      if (sc.killFeed.length > 6) sc.killFeed.shift();
    }
  };

  const applyHeal: Actors['applyHeal'] = (ev) => {
    const f = sc.byId[ev.id];
    if (!f) return;
    commit(ev);
    sc.damages.push({ x: f.x, y: f.y, amount: ev.amount, crit: false, heal: true, t0: now(), life: 0.9 });
    playSfx('medkit', mix(0.5, ev.id));
  };

  const applyShield: Actors['applyShield'] = (ev) => {
    const f = sc.byId[ev.id];
    if (!f) return;
    f.shield = ev.amount;
    sc.damages.push({ x: f.x, y: f.y, amount: ev.amount, crit: false, absorb: true, gain: true, t0: now(), life: 0.9 });
  };

  const applyKnife: Actors['applyKnife'] = (ev) => {
    const s = sc.byId[ev.shooter]; const tg = sc.byId[ev.target];
    if (!s || !tg) return;
    const frac = state.mutualKnifers.has(ev.shooter) ? 0.5 : KNIFE_TRAVEL_FRAC;
    const strike = () => {
      s.aim = Math.atan2(tg.y - s.y, tg.x - s.x);
      s.recoil = 1;
      sc.slashes.push({ x: tg.x, y: tg.y - 70, angle: s.aim, t0: now(), life: 0.28 });
      commit(ev);
      if (ev.damage > 0) {
        playSfx('knife_stab', mix(0.6, ev.shooter, ev.target));
        const toHp = drainHit(tg, ev.damage, ev.absorbed);
        if (toHp > 0) popDamage(tg, toHp, ev.killed);
        if (!opts.replay && ev.target === opts.getYouId()) { opts.onYouHit(); playSfx('armor_hit', 0.5); }
      }
      if (ev.punish && ev.punish > 0) returnFire(tg, s, ev.punish, ev.shooter, ev.punishAbsorbed);
    };
    s.lunge = { t0: now(), homeX: s.x, homeY: s.y, targetId: ev.target, frac, dur: KNIFE_LUNGE, struck: false, strike };
    playSfx('knife_deploy', mix(0.5, ev.shooter, ev.target));
  };

  const applySmokeThrow: Actors['applySmokeThrow'] = (ev, land) => {
    const s = sc.byId[ev.shooter]; const tg = sc.byId[ev.target];
    if (!s || !tg) return;
    const h = handOf(sc, s);
    commit(ev);
    sc.grenades.push({ fromX: h.x, fromY: h.y, toX: tg.spawnX, toY: tg.y, t0: now(), land, smoke: true });
    state.pendingSmokes.push({ at: land, target: ev.target });
    playSfx('grenade_throw', mix(0.4, ev.shooter, ev.target));
  };

  const applyFlashThrow: Actors['applyFlashThrow'] = (ev, land) => {
    const s = sc.byId[ev.shooter];
    const center = sc.byId[ev.targets[0]] ?? s;
    if (!s || !center) return;
    const h = handOf(sc, s);
    commit(ev);
    sc.grenades.push({ fromX: h.x, fromY: h.y, toX: center.x, toY: center.y, t0: now(), land, flash: true });
    playSfx('grenade_throw', mix(0.4, ev.shooter, ...ev.targets));
  };

  const applyFlashLand: Actors['applyFlashLand'] = (ev, round) => {
    const t0 = now();
    let cx = 0; let cy = 0; let k = 0;
    for (const id of ev.targets) {
      const f = sc.byId[id];
      if (!f) continue;
      state.blindGlows.set(id, { until: round + 1, bornAt: t0 });
      cx += f.x; cy += f.y; k += 1;
    }
    if (k) sc.flashbangs.push({ x: cx / k, y: cy / k - 60, t0, life: 0.5 });
    playSfx('flash_explode', mix(0.5, ev.shooter, ...ev.targets));
    const youId = opts.getYouId();
    if (!opts.replay && youId != null && ev.targets.includes(youId)) playSfx('flash_blind', 0.08);
  };

  const applyGrenadeThrow: Actors['applyGrenadeThrow'] = (ev, land, toX, toY) => {
    const s = sc.byId[ev.shooter];
    const h = s ? handOf(sc, s) : { x: toX, y: toY };
    sc.grenades.push({ fromX: h.x, fromY: h.y, toX, toY, t0: now(), land });
    playSfx('grenade_throw', mix(0.5, ev.shooter));
  };

  return { applyShot, applyDeath, applyHeal, applyShield, applyKnife, applySmokeThrow, applyFlashThrow, applyFlashLand, applyGrenadeThrow, popDamage, popAbsorb, drainHit, commit };
};
